import React, { useEffect, useState } from 'react'
import { Button, Message } from 'semantic-ui-react'
import { createActivity } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { downloadText } from 'utility/crm/csv'
import { toDateInput } from 'utility/crm/dates'
import { buildIcs, icsFilename } from 'utility/crm/ics'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmToast } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'
import { ChipButton, Row } from './ui'

const label = { display: 'block', fontWeight: 600, margin: '10px 0 4px' }
const field = { width: '100%', padding: 8 }
const DURATIONS = [15, 30, 45, 60, 90]
const FOLLOW_UPS = [
    [0, 'No follow-up'],
    [1, '1 day'],
    [3, '3 days'],
    [7, '1 week'],
]

const pad = n => String(n).padStart(2, '0')
/** Local "2026-10-12T14:30:00": the API stores calendar times as entered, without a zone. */
const localIso = date =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`

/**
 * Schedule a meeting: downloads an .ics (UTC times) the user can open in any calendar, then logs the
 * meeting on the contact's timeline, optionally with a follow-up.
 * @param {{ contact: import('utility/crm/types').CrmContact|null, onClose: () => void }} props
 */
export const MeetingDialog = ({ contact, onClose }) => {
    const { successToast } = useCrmToast()
    const [title, setTitle] = useState('')
    const [date, setDate] = useState('')
    const [time, setTime] = useState('10:00')
    const [duration, setDuration] = useState(30)
    const [where, setWhere] = useState('')
    const [notes, setNotes] = useState('')
    const [days, setDays] = useState(0)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!contact) return
        setTitle(`Meeting with ${contact.Name}`)
        setDate(toDateInput(new Date(Date.now() + 86400000)))
        setTime('10:00')
        setDuration(30)
        setWhere('')
        setNotes('')
        setDays(0)
        setError(null)
    }, [contact?.ContactId]) // eslint-disable-line

    // Local date + time -> the exact moment (the .ics file converts it to UTC).
    const start = date && time ? new Date(`${date}T${time}:00`) : null
    const validStart = start && !Number.isNaN(start.getTime())
    const canSubmit = Boolean(title.trim()) && validStart && !busy

    const submit = async () => {
        if (!canSubmit) return
        setBusy(true)
        setError(null)
        try {
            const ics = buildIcs({
                uid: `crm-${contact.ContactId}-${start.getTime()}@eauditor`,
                title: title.trim(),
                start,
                durationMinutes: duration,
                description: notes,
                location: where,
                attendee: { name: contact.Name, email: contact.Email },
            })
            downloadText(icsFilename(title, start), ics, 'text/calendar;charset=utf-8', false)
            await createActivity({
                Type: 'meeting',
                ContactId: contact.ContactId,
                Title: title.trim(),
                Body: [notes.trim(), where.trim() ? `Where: ${where.trim()}` : '', `${duration} min`].filter(Boolean).join('\n'),
                Date: localIso(start),
                FollowUpInDays: days || undefined,
            })
            successToast('Calendar file downloaded and meeting logged')
            invalidateCrm(...ALL_CRM_SCOPES)
            onClose()
        } catch (err) {
            setError(toApiError(err).message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <CrmModal
            open={Boolean(contact)}
            onClose={busy ? () => {} : onClose}
            title={contact ? `Meeting · ${contact.Name}` : 'Meeting'}
            width={540}
            footer={
                <>
                    <Button type="button" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="button" primary onClick={submit} loading={busy} disabled={!canSubmit}>
                        Download .ics &amp; log meeting
                    </Button>
                </>
            }
        >
            {error ? (
                <Message negative role="alert">
                    {error}
                </Message>
            ) : null}
            <label htmlFor="meet-title" style={label}>
                Title *
            </label>
            <input id="meet-title" value={title} onChange={e => setTitle(e.target.value)} style={field} maxLength={150} />

            <Row $gap={12} $wrap style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 150 }}>
                    <label htmlFor="meet-date" style={label}>
                        Date
                    </label>
                    <input id="meet-date" type="date" value={date} onChange={e => setDate(e.target.value)} style={field} />
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                    <label htmlFor="meet-time" style={label}>
                        Start time
                    </label>
                    <input id="meet-time" type="time" value={time} onChange={e => setTime(e.target.value)} style={field} />
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                    <label htmlFor="meet-duration" style={label}>
                        Length
                    </label>
                    <select id="meet-duration" value={duration} onChange={e => setDuration(Number(e.target.value))} style={field}>
                        {DURATIONS.map(d => (
                            <option key={d} value={d}>
                                {d} min
                            </option>
                        ))}
                    </select>
                </div>
            </Row>
            {!validStart ? (
                <p role="alert" style={{ color: '#dc2626', margin: '2px 0', fontSize: '0.8rem' }}>
                    Choose a date and a start time.
                </p>
            ) : null}

            <label htmlFor="meet-where" style={label}>
                Location or video link
            </label>
            <input id="meet-where" value={where} onChange={e => setWhere(e.target.value)} style={field} maxLength={200} />

            <label htmlFor="meet-notes" style={label}>
                Agenda / notes
            </label>
            <textarea id="meet-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} style={field} />

            <Row $gap={6} $wrap role="group" aria-label="Follow up in" style={{ marginTop: 12 }}>
                <span style={{ opacity: 0.7, fontSize: '0.85rem' }}>Follow up in</span>
                {FOLLOW_UPS.map(([value, text]) => (
                    <ChipButton key={value} type="button" $active={days === value} aria-pressed={days === value} onClick={() => setDays(value)}>
                        {text}
                    </ChipButton>
                ))}
            </Row>
        </CrmModal>
    )
}
